<div class="holiday-settings">
  <div class="holiday-settings__intro">
    <h5 class="holiday-settings__title">{{ 'Settings::Holidays:Title' | abpLocalization }}</h5>
    <p class="holiday-settings__description small">
      {{ 'Settings::Holidays:PageDescription' | abpLocalization }}
    </p>
  </div>

  <div class="holiday-settings__panels">
    <!-- Reference calendar -->
    <section class="holiday-settings__panel">
      <header class="holiday-settings__panel-header">
        <span class="holiday-settings__panel-title">
          {{ 'Settings::Holidays:ReferenceCalendar' | abpLocalization }}
        </span>
        @if (isReferenceCalendarEnabled && referenceHolidays.length) {
          <span class="holiday-settings__panel-meta">
            <!-- Counts are stringified: ABP's localization pipe silently drops falsy parameters. -->
            {{
              'Settings::Holidays:AddedOfTotal'
                | abpLocalization
                  : addedReferenceCount.toString()
                  : referenceHolidays.length.toString()
            }}
          </span>
        }
      </header>

      @if (!isReferenceCalendarEnabled) {
        <div class="holiday-settings__notice small">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          {{ 'Settings::Holidays:ReferenceCalendarUnavailable' | abpLocalization }}
        </div>
      } @else {
        <div class="holiday-settings__filters">
          <div class="holiday-settings__filter">
            <label class="form-label" [attr.for]="null">
              {{ 'Settings::Holidays:Country' | abpLocalization }}
            </label>
            <cx-select
              class="w-100"
              [options]="countries"
              optionLabel="name"
              optionValue="code"
              [ngModel]="selectedCountryCode"
              [loading]="isLoadingOptions"
              [disabled]="isLoadingOptions"
              (valueChange)="onCountryChange($any($event))" />
          </div>
          <div class="holiday-settings__filter holiday-settings__filter--year">
            <label class="form-label" [attr.for]="null">
              {{ 'Settings::Holidays:Year' | abpLocalization }}
            </label>
            <cx-select
              class="w-100"
              [options]="yearOptions"
              optionLabel="label"
              optionValue="value"
              [ngModel]="selectedYear"
              [loading]="isLoadingOptions"
              [disabled]="isLoadingOptions"
              (valueChange)="onYearChange($any($event))" />
          </div>
        </div>

        <div class="holiday-settings__toolbar">
          <span class="holiday-settings__search">
            <i class="pi pi-search" aria-hidden="true"></i>
            <input
              type="search"
              class="form-control form-control-sm"
              [value]="searchTerm"
              [placeholder]="'Settings::Holidays:SearchPlaceholder' | abpLocalization"
              (input)="onSearchInput($any($event.target).value)" />
          </span>
          <label class="holiday-settings__inline-check small">
            <input
              class="form-check-input"
              type="checkbox"
              [checked]="hideAdded"
              (change)="onHideAddedChange($any($event.target).checked)" />
            {{ 'Settings::Holidays:HideAdded' | abpLocalization }}
          </label>
        </div>

        <div class="holiday-settings__table-wrapper">
          <p-table
            [value]="visibleReferenceHolidays"
            dataKey="key"
            [loading]="isLoadingReference"
            [scrollable]="true"
            scrollHeight="flex"
            [stripedRows]="true"
            [tableStyle]="{ 'min-width': '100%' }"
            styleClass="holiday-settings__table">
            <ng-template #header>
              <tr>
                <th class="holiday-settings__cell-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    [attr.aria-label]="'Settings::Holidays:SelectAll' | abpLocalization"
                    [checked]="isAllSelected"
                    [indeterminate]="isSomeSelected"
                    [disabled]="!canEdit || !selectableVisibleHolidays.length"
                    (change)="onToggleAll()" />
                </th>
                <th>{{ 'Settings::Holidays:DateAndName' | abpLocalization }}</th>
                <th class="holiday-settings__cell-status text-end">
                  {{ 'Settings::Holidays:Status' | abpLocalization }}
                </th>
              </tr>
            </ng-template>

            <ng-template #body let-holiday>
              <tr [class.holiday-settings__row--added]="holiday.isAdded">
                <td class="holiday-settings__cell-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    [attr.aria-label]="'Settings::Holidays:Select' | abpLocalization"
                    [checked]="holiday.isAdded || holiday.selected"
                    [disabled]="holiday.isAdded || !canEdit"
                    (change)="onToggleHoliday(holiday)" />
                </td>
                <td>
                  <div class="holiday-settings__name">{{ holiday.name }}</div>
                  <div class="holiday-settings__date small">
                    {{ holiday.date | displayDate: 'UTC' }}
                    @if (holiday.weekday) {
                      <span class="holiday-settings__weekday">· {{ holiday.weekday }}</span>
                    }
                  </div>
                </td>
                <td class="holiday-settings__cell-status text-end">
                  @if (holiday.isAdded) {
                    <span class="holiday-settings__badge holiday-settings__badge--added">
                      {{ 'Settings::Holidays:StatusAdded' | abpLocalization }}
                    </span>
                  } @else {
                    <span class="holiday-settings__badge">
                      {{ 'Settings::Holidays:StatusAvailable' | abpLocalization }}
                    </span>
                  }
                </td>
              </tr>
            </ng-template>

            <ng-template #emptymessage>
              @if (!isLoadingReference) {
                <tr>
                  <td colspan="3" class="holiday-settings__empty small">
                    @if (referenceHolidays.length) {
                      {{ 'Settings::Holidays:NoSearchResult' | abpLocalization }}
                    } @else {
                      {{ 'Settings::Holidays:NotFound' | abpLocalization }}
                    }
                  </td>
                </tr>
              }
            </ng-template>
          </p-table>
        </div>

        <footer class="holiday-settings__panel-footer">
          <span class="small text-muted">
            @if (selectedCount) {
              {{ 'Settings::Holidays:SelectedCount' | abpLocalization: selectedCount.toString() }}
            } @else {
              {{ 'Settings::Holidays:NoneSelected' | abpLocalization }}
            }
          </span>
          <button
            type="button"
            class="btn btn-primary btn-sm holiday-settings__action-button"
            [disabled]="!canAddSelected"
            (click)="onAddSelected()">
            <i class="pi pi-plus" aria-hidden="true"></i>
            {{ 'Settings::Holidays:AddSelected' | abpLocalization }}
          </button>
        </footer>
      }
    </section>

    <!-- Tenant holiday calendar -->
    <section class="holiday-settings__panel">
      <header class="holiday-settings__panel-header">
        <span class="holiday-settings__panel-title">
          {{ 'Settings::Holidays:AddedHolidays' | abpLocalization }}
          <span class="holiday-settings__badge holiday-settings__badge--count">
            {{ 'Settings::Holidays:TotalCount' | abpLocalization: addedHolidays.length.toString() }}
          </span>
        </span>
      </header>

      <div class="holiday-settings__chips">
        <button
          type="button"
          class="holiday-settings__chip"
          [class.holiday-settings__chip--active]="sourceFilter === ALL_SOURCES"
          (click)="onSourceFilterChange(ALL_SOURCES)">
          {{ 'Settings::Holidays:FilterAll' | abpLocalization }}
        </button>
        @for (country of countries; track country.code) {
          <button
            type="button"
            class="holiday-settings__chip"
            [class.holiday-settings__chip--active]="sourceFilter === country.code"
            (click)="onSourceFilterChange(country.code)">
            {{ country.name }}
          </button>
        }
        <button
          type="button"
          class="holiday-settings__chip"
          [class.holiday-settings__chip--active]="sourceFilter === CUSTOM_SOURCE"
          (click)="onSourceFilterChange(CUSTOM_SOURCE)">
          {{ 'Settings::Holidays:Custom' | abpLocalization }}
        </button>
      </div>

      <div class="holiday-settings__table-wrapper">
        <p-table
          [value]="visibleAddedHolidays"
          dataKey="id"
          [loading]="isLoadingAdded"
          [scrollable]="true"
          scrollHeight="flex"
          [stripedRows]="true"
          [tableStyle]="{ 'min-width': '100%' }"
          styleClass="holiday-settings__table">
          <ng-template #header>
            <tr>
              <th>{{ 'Settings::Holidays:DateAndName' | abpLocalization }}</th>
              <th class="holiday-settings__cell-source">
                {{ 'Settings::Holidays:Source' | abpLocalization }}
              </th>
              <th class="holiday-settings__cell-actions"></th>
            </tr>
          </ng-template>

          <ng-template #body let-holiday>
            <tr>
              <td>
                <div class="holiday-settings__name">{{ holiday.name }}</div>
                <div class="holiday-settings__date small">
                  {{ holiday.date | displayDate: 'UTC' }}
                  @if (holiday.weekday) {
                    <span class="holiday-settings__weekday">· {{ holiday.weekday }}</span>
                  }
                </div>
              </td>
              <td class="holiday-settings__cell-source">
                @if (holiday.isCustom) {
                  <span class="holiday-settings__badge holiday-settings__badge--custom">
                    {{ 'Settings::Holidays:Custom' | abpLocalization }}
                  </span>
                } @else {
                  <span class="holiday-settings__badge">
                    {{ holiday.countryName || holiday.countryCode }}
                  </span>
                }
              </td>
              <td class="holiday-settings__cell-actions text-end">
                @if (canEdit) {
                  <button
                    type="button"
                    class="holiday-settings__remove"
                    [attr.aria-label]="'Settings::Holidays:Remove' | abpLocalization"
                    [title]="'Settings::Holidays:Remove' | abpLocalization"
                    [disabled]="isSubmitting"
                    (click)="onRemove(holiday)">
                    <i class="pi pi-trash" aria-hidden="true"></i>
                  </button>
                }
              </td>
            </tr>
          </ng-template>

          <ng-template #emptymessage>
            @if (!isLoadingAdded) {
              <tr>
                <td colspan="3" class="holiday-settings__empty small">
                  @if (addedHolidays.length) {
                    {{ 'Settings::Holidays:EmptyFilteredCalendar' | abpLocalization }}
                  } @else {
                    {{ 'Settings::Holidays:EmptyCalendar' | abpLocalization }}
                  }
                </td>
              </tr>
            }
          </ng-template>
        </p-table>
      </div>

      @if (canEdit) {
        <form class="holiday-settings__custom" [formGroup]="customForm" (ngSubmit)="onAddCustom()">
          <div class="holiday-settings__custom-header">
            <i class="pi pi-calendar-plus" aria-hidden="true"></i>
            <span class="holiday-settings__custom-title">
              {{ 'Settings::Holidays:CustomHoliday' | abpLocalization }}
            </span>
            <span class="small text-muted">
              {{ 'Settings::Holidays:CustomHolidayHint' | abpLocalization }}
            </span>
          </div>
          <div class="holiday-settings__custom-fields">
            <p-datepicker
              formControlName="date"
              [dateFormat]="primeDateFormat"
              [placeholder]="datePlaceholder"
              styleClass="w-100"
              appendTo="body"
              [fluid]="true"
              [showIcon]="true"
              [showOnFocus]="false" />
            <input
              type="text"
              class="form-control"
              formControlName="name"
              [maxlength]="maxNameLength"
              [placeholder]="'Settings::Holidays:CustomNamePlaceholder' | abpLocalization" />
            <button
              type="submit"
              class="btn btn-primary btn-sm holiday-settings__action-button"
              [disabled]="!canAddCustom">
              <i class="pi pi-plus" aria-hidden="true"></i>
              {{ 'Settings::Holidays:AddHoliday' | abpLocalization }}
            </button>
          </div>
        </form>
      }
    </section>
  </div>
</div>
